// ═══════════════════════════════════════════════════════════════════════════
// WRITTN - Main Application v2 (Premium + Auth)
// ═══════════════════════════════════════════════════════════════════════════
// A spiritually resonant, TikTok-style Bible verse experience
// ═══════════════════════════════════════════════════════════════════════════

import './styles/main.css';

// Config
import { SETTINGS } from './config/settings.js';
import { PROMPTS } from './config/prompts.js';

// Services
import * as storage from './services/storage.js';
import * as verseEngine from './services/verseEngine.js';
import { isAuthenticated, onAuthChange, getCurrentUser, logOut } from './services/auth.js';
import * as firestore from './services/firestore.js';
import * as analytics from './services/analytics.js';
import { initCloudSync } from './services/cloudSync.js';

// Components
import { icons } from './components/icons.js';
import { createOnboarding } from './components/Onboarding.js';
import { createVerseCard } from './components/VerseCard.js';
import { createActionBar, updateHeartState } from './components/ActionBar.js';
import { showDiveDeeper } from './components/DiveDeeper.js';
import { createReflectionCard } from './components/ReflectionScreen.js';
import { setViewVerseCallback } from './components/Favorites.js';
import { showProfile, applyTheme, setProfileViewVerseCallback } from './components/Profile.js';
import { showAuthPrompt } from './components/AuthPrompt.js';
import { showExplore } from './components/Explore.js';
import { showShareMenu } from './components/ShareMenu.js';
import { showSoakTimeOnboarding } from './components/SoakTimeOnboarding.js';
import { showReflectOverlay } from './components/ReflectOverlay.js';
import { initInstallPrompt } from './components/InstallPrompt.js';

// ─────────────────────────────────────────────────────────────────────────
// STATE
// ─────────────────────────────────────────────────────────────────────────

let state = {
  currentVerse: null,
  currentVerseIndex: 0,
  verses: [],
  isFirstSession: false,
  sessionVerseCount: 0,
  rapidScrollTimes: [],
  divineVerseTimer: null,
  isShowingReflection: false,
  user: null, // Current authenticated user
  // Soak time state
  verseStartTime: null,
  canScroll: true,
  soakTimerInterval: null,
};

// ─────────────────────────────────────────────────────────────────────────
// INITIALIZATION
// ─────────────────────────────────────────────────────────────────────────

async function init() {
  // Initialize analytics first
  await analytics.initAnalytics();

  // Initialize cloud sync (starts listening when user signs in)
  await initCloudSync();

  // Initialize PWA install prompt tracking
  initInstallPrompt();

  // Apply saved theme
  const savedTheme = storage.getTheme();
  applyTheme(savedTheme);

  // Listen for auth state changes
  onAuthChange((user) => {
    state.user = user;
    updateHeaderForUser(user);

    // Refresh heart state when auth changes (e.g., after signout)
    if (state.currentVerse) {
      const isSaved = storage.isFavorite(state.currentVerse.reference);
      updateHeartState(isSaved);
    }
  });

  // Register callback for viewing saved verses from Favorites panel
  setViewVerseCallback((verse) => {
    navigateToVerse(verse);
  });

  // Same navigation when tapping a saved verse inside the Profile panel (#42)
  setProfileViewVerseCallback((verse) => {
    navigateToVerse(verse);
  });

  // Create app structure
  const app = document.getElementById('app');
  app.innerHTML = `
    <!-- Ambient Background -->
    <div class="ambient-background"></div>
    
    <div class="app-container">
      <header class="header">
        <div class="header-logo">
          <div class="header-logo-icon">
            ${icons.logo}
          </div>
          <span class="header-logo-text">Writtn</span>
        </div>
        <div class="header-actions">
          <button class="header-btn" id="explore-btn" aria-label="Explore community">
            ${icons.explore}
          </button>
          <button class="header-btn" id="profile-btn" aria-label="Profile and settings">
            ${icons.user}
          </button>
        </div>
      </header>
      
      <main class="verse-feed" id="verse-feed">
        <!-- Verses will be injected here -->
      </main>
    </div>

    <!-- Screen-reader announcement of the current verse (#74) -->
    <div id="verse-live-region" class="sr-only" aria-live="polite" aria-atomic="true"></div>
  `;

  // Create action bar
  const actionBar = createActionBar({
    onReflect: handleReflect,
    onContext: handleDiveDeeper,
    onHeart: handleHeart,
    onShare: handleShare,
  });
  app.appendChild(actionBar);

  // Setup header buttons
  document.getElementById('explore-btn').addEventListener('click', showExplore);
  document.getElementById('profile-btn').addEventListener('click', () => showProfile('settings'));

  // Check if first visit
  if (SETTINGS.enableOnboarding && !storage.hasCompletedOnboarding()) {
    const onboarding = createOnboarding(() => {
      startApp();
    });
    document.body.appendChild(onboarding);
  } else {
    startApp();
  }
}

function updateHeaderForUser(user) {
  // For future: show user avatar or login button in header
  // For now, we just track the state
}

function startApp() {
  // Determine if first session (show curated verses)
  state.isFirstSession = !storage.hasSeenFirstSession();

  if (state.isFirstSession) {
    state.verses = verseEngine.getFirstSessionVerses();
    storage.setFirstSessionComplete();
  } else {
    // Load initial batch of random verses
    state.verses = verseEngine.getMultipleVerses(20);
  }

  // Render initial verses
  renderVerses();

  // Setup scroll handling
  setupScrollHandling();

  // Show soak time onboarding if not seen
  if (!storage.hasSoakTimeOnboarded()) {
    // Delay slightly to let the app settle
    setTimeout(() => {
      showSoakTimeOnboarding();
    }, 1500);
  }
}

// ─────────────────────────────────────────────────────────────────────────
// VERSE RENDERING
// ─────────────────────────────────────────────────────────────────────────

function renderVerses() {
  const feed = document.getElementById('verse-feed');

  state.verses.forEach((verse, index) => {
    const card = createVerseCard(verse);
    feed.appendChild(card);
  });

  // Set initial current verse
  if (state.verses.length > 0) {
    setCurrentVerse(state.verses[0], 0);
  }
}

function loadMoreVerses() {
  // Check if we should show reflection
  if (SETTINGS.enableSessionEndRitual &&
    state.sessionVerseCount >= SETTINGS.versesPerSession &&
    !state.isShowingReflection) {
    showReflection();
    return;
  }

  const feed = document.getElementById('verse-feed');
  const newVerses = verseEngine.getMultipleVerses(10, state.verses.map(v => v.reference));

  newVerses.forEach(verse => {
    // Check if should show Divine Verse
    const isDivine = verseEngine.shouldShowDivineVerse();
    const verseToAdd = isDivine ? verseEngine.getDivineVerse() : verse;

    state.verses.push(verseToAdd);
    const card = createVerseCard(verseToAdd, { isDivine });
    feed.appendChild(card);
  });
}

function showReflection() {
  state.isShowingReflection = true;

  const feed = document.getElementById('verse-feed');
  const reflectionCard = createReflectionCard(() => {
    // Remove reflection card
    const card = document.getElementById('reflection-card');
    if (card) card.remove();

    // Reset session
    state.sessionVerseCount = 0;
    state.isShowingReflection = false;

    // Load more verses
    loadMoreVerses();
  });

  feed.appendChild(reflectionCard);
}

function setCurrentVerse(verse, index) {
  state.currentVerse = verse;
  state.currentVerseIndex = index;

  // Announce the current verse to assistive technology (#74).
  const liveRegion = document.getElementById('verse-live-region');
  if (liveRegion) {
    liveRegion.textContent = `${verse.reference}. ${verse.text}`;
  }

  // Update heart state
  const isSaved = storage.isFavorite(verse.reference);
  updateHeartState(isSaved);

  // Track verse view
  storage.markVerseSeen(verse.reference);
  verseEngine.trackVerseViewed();

  // Analytics: Track verse view start
  analytics.startVerseView(verse.reference);

  // Increment session count
  state.sessionVerseCount++;

  // Start soak timer
  startSoakTimer();
}

// ─────────────────────────────────────────────────────────────────────────
// SOAK TIME MANAGEMENT
// ─────────────────────────────────────────────────────────────────────────

function startSoakTimer() {
  // Clear any existing timer
  if (state.soakTimerInterval) {
    clearInterval(state.soakTimerInterval);
  }

  const soakTime = storage.getSoakTime() * 1000; // Convert to ms
  state.verseStartTime = Date.now();
  state.canScroll = false;

  // Update progress bar
  const feed = document.getElementById('verse-feed');
  const currentCard = feed.querySelector(
    `[data-reference="${state.currentVerse?.reference}"]`
  );

  // Add timer UI to current card's inner container
  const cardInner = currentCard?.querySelector('.verse-card-inner');
  let timerContainer = cardInner?.querySelector('.soak-timer-container');
  if (cardInner && !timerContainer) {
    timerContainer = document.createElement('div');
    timerContainer.className = 'soak-timer-container';
    timerContainer.innerHTML = '<div class="soak-timer-progress"></div>';
    cardInner.appendChild(timerContainer);
  }

  const progressBar = timerContainer?.querySelector('.soak-timer-progress');

  // Update progress every 100ms
  state.soakTimerInterval = setInterval(() => {
    const elapsed = Date.now() - state.verseStartTime;
    const progress = Math.min((elapsed / soakTime) * 100, 100);

    if (progressBar) {
      progressBar.style.width = `${progress}%`;

      if (progress >= 100) {
        progressBar.classList.add('complete');
      }
    }

    // Enable scrolling after soak time
    if (elapsed >= soakTime) {
      state.canScroll = true;
      clearInterval(state.soakTimerInterval);
      state.soakTimerInterval = null;
    }
  }, 100);
}

function showSoakToast() {
  // Don't show if already showing
  if (document.querySelector('.soak-toast')) return;

  const toast = document.createElement('div');
  toast.className = 'soak-toast';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.innerHTML = `
    <span class="soak-toast-icon">🕐</span>
    <span class="soak-toast-text">Take a moment with this verse</span>
  `;

  document.body.appendChild(toast);

  requestAnimationFrame(() => {
    toast.classList.add('active');
  });

  // Log early scroll attempt
  if (state.currentVerse) {
    const elapsed = Date.now() - state.verseStartTime;
    analytics.logEarlyScroll(state.currentVerse.reference, elapsed, storage.getSoakTime() * 1000);
  }

  // Remove after delay
  setTimeout(() => {
    toast.classList.remove('active');
    setTimeout(() => toast.remove(), 300);
  }, 2000);
}

function navigateToVerse(verse) {
  // Check if verse is already in the feed
  const feed = document.getElementById('verse-feed');
  const existingCard = feed.querySelector(`[data-reference="${verse.reference}"]`);

  if (existingCard) {
    // Scroll to existing card
    existingCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const existingIndex = Array.from(feed.children).indexOf(existingCard);
    setCurrentVerse(verse, existingIndex);
  } else {
    // Add verse to the beginning of the feed and scroll to it
    state.verses.unshift(verse);

    const card = createVerseCard(verse);
    card.dataset.reference = verse.reference;
    feed.insertBefore(card, feed.firstChild);

    // Set as current and scroll to top
    setCurrentVerse(verse, 0);
    feed.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

// ─────────────────────────────────────────────────────────────────────────
// SCROLL HANDLING - TikTok/Reels Style
// ─────────────────────────────────────────────────────────────────────────

function setupScrollHandling() {
  const feed = document.getElementById('verse-feed');
  let scrollTimeout = null;
  let lastScrollTop = 0;

  // ─────────────────────────────────────────────
  // SOAK TIME SCROLL BLOCKING
  // ─────────────────────────────────────────────

  // Track if we're actively blocking to avoid re-triggering
  let isBlocking = false;

  // Touch event handling for mobile
  let touchStartY = 0;
  feed.addEventListener('touchstart', (e) => {
    touchStartY = e.touches[0].clientY;
    lastScrollTop = feed.scrollTop;
  }, { passive: true });

  feed.addEventListener('touchmove', (e) => {
    if (!state.canScroll) {
      // Strictly prevent scrolling during soak time
      if (e.cancelable) e.preventDefault();

      if (!isBlocking) {
        const touchY = e.touches[0].clientY;
        const deltaY = touchStartY - touchY;

        // Only show toast if trying to scroll to a new verse (significant scroll)
        if (Math.abs(deltaY) > 30) {
          isBlocking = true;
          showSoakToast();

          // Reset blocking after a short delay
          setTimeout(() => {
            isBlocking = false;
          }, 2000);
        }
      }
    }
  }, { passive: false });

  // Restore scroll position on touchend if blocked (fallback)
  feed.addEventListener('touchend', () => {
    if (!state.canScroll) {
      // Snap back to current verse just in case
      const currentCard = feed.querySelector(
        `[data-reference="${state.currentVerse?.reference}"]`
      );
      if (currentCard) {
        currentCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, { passive: true });

  // Mouse wheel for desktop
  feed.addEventListener('wheel', (e) => {
    if (!state.canScroll) {
      if (e.cancelable) e.preventDefault();

      if (!isBlocking) {
        if (Math.abs(e.deltaY) > 50) {
          isBlocking = true;
          showSoakToast();

          setTimeout(() => {
            isBlocking = false;
          }, 2000);
        }
      }
    }
  }, { passive: false });

  // ─────────────────────────────────────────────
  // INTERSECTION OBSERVER FOR VERSE TRACKING
  // ─────────────────────────────────────────────

  // Use IntersectionObserver for verse tracking
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
        const card = entry.target;
        const reference = card.dataset.reference;

        if (reference) {
          const index = state.verses.findIndex(v => v.reference === reference);
          if (index !== -1 && index !== state.currentVerseIndex) {
            // Only allow verse change if can scroll
            if (state.canScroll) {
              setCurrentVerse(state.verses[index], index);
            }
          }
        }

        // Check if near end for loading more
        if (index >= state.verses.length - 3) {
          loadMoreVerses();
        }
      }
    });
  }, {
    threshold: 0.5,
    root: feed,
  });

  // Observe all verse cards
  feed.querySelectorAll('.verse-card').forEach(card => {
    observer.observe(card);
  });

  // Re-observe when new cards are added
  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.classList?.contains('verse-card') || node.classList?.contains('reflection-card')) {
          observer.observe(node);
        }
      });
    });
  });

  mutationObserver.observe(feed, { childList: true });

  // Track for rapid scroll detection
  if (SETTINGS.detectRapidScrolling) {
    feed.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        trackRapidScroll();
      }, 100);
    }, { passive: true });
  }
}

function trackRapidScroll() {
  const now = Date.now();
  state.rapidScrollTimes.push(now);

  // Keep only last 10 seconds of scroll events
  state.rapidScrollTimes = state.rapidScrollTimes.filter(t => now - t < 10000);

  // Check if scrolling too fast (many scroll events = rapid swiping)
  if (state.rapidScrollTimes.length >= SETTINGS.rapidScrollThreshold * 2) {
    showRapidScrollIntervention();
    state.rapidScrollTimes = [];
  }
}

function showRapidScrollIntervention() {
  // Only show once per session
  if (document.querySelector('.rapid-scroll-overlay')) return;

  const message = PROMPTS.rapidScrollMessages[
    Math.floor(Math.random() * PROMPTS.rapidScrollMessages.length)
  ];

  const overlay = document.createElement('div');
  overlay.className = 'rapid-scroll-overlay';

  overlay.innerHTML = `
    <div class="rapid-scroll-content">
      <p class="rapid-scroll-message">${message}</p>
      <button class="rapid-scroll-btn">Continue slowly</button>
    </div>
  `;

  document.body.appendChild(overlay);

  requestAnimationFrame(() => {
    overlay.classList.add('active');
  });

  const btn = overlay.querySelector('.rapid-scroll-btn');
  btn.addEventListener('click', () => {
    overlay.classList.remove('active');
    setTimeout(() => overlay.remove(), 300);
  });
}

// ─────────────────────────────────────────────────────────────────────────
// ACTION HANDLERS (with Auth Integration)
// ─────────────────────────────────────────────────────────────────────────

function handleHeart() {
  if (!state.currentVerse) return;

  // Check if user is authenticated
  if (!isAuthenticated()) {
    // Show auth prompt with the save action as callback
    showAuthPrompt(() => {
      // After successful auth, perform the save
      performSave();
    }, 'save');
    return;
  }

  performSave();
}

async function performSave() {
  const isSaved = firestore.isFavorite(state.currentVerse.reference);

  if (isSaved) {
    await firestore.removeFavorite(state.currentVerse.reference);
    analytics.logFavorite(state.currentVerse.reference, false);
    showToast('Removed');
  } else {
    await firestore.saveFavorite(state.currentVerse);
    storage.addSessionFavorite(state.currentVerse);
    analytics.logFavorite(state.currentVerse.reference, true);
    showToast('Saved');

    // Haptic feedback
    if (SETTINGS.enableHaptics && navigator.vibrate) {
      navigator.vibrate(10);
    }
  }

  updateHeartState(!isSaved);
}

function handleShare() {
  if (!state.currentVerse) return;
  showShareMenu(state.currentVerse);
}

function handleDiveDeeper() {
  if (!state.currentVerse) return;
  analytics.logDiveDeeperOpen(state.currentVerse.reference);
  showDiveDeeper(state.currentVerse);
}

function handleReflect() {
  if (!state.currentVerse) return;
  showReflectOverlay(state.currentVerse);
}

// ─────────────────────────────────────────────────────────────────────────
// TOAST NOTIFICATIONS
// ─────────────────────────────────────────────────────────────────────────

function showToast(message) {
  // Remove existing toast
  const existing = document.querySelector('.toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.className = 'toast';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.textContent = message;

  document.body.appendChild(toast);

  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateX(-50%) translateY(20px)';
    toast.style.transition = 'all 0.3s ease';
    setTimeout(() => toast.remove(), 300);
  }, 2500);
}

// ─────────────────────────────────────────────────────────────────────────
// SERVICE WORKER REGISTRATION
// ─────────────────────────────────────────────────────────────────────────

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(reg => console.log('SW registered'))
      .catch(err => console.log('SW registration failed:', err));
  });
}

// ─────────────────────────────────────────────────────────────────────────
// START
// ─────────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', init);
